import React, { useEffect, useRef, useState } from "react";
import { ChevronUp, X } from "lucide-react";

import { IconButton } from "@/design-system/components/IconButton/IconButton";
import { Popover } from "@/design-system/components/Popover/Popover";
import { Progress } from "@/design-system/components/Progress/Progress";
import {
  getRecordedApplicableSteps,
  getStepProgress,
  sessionHasProgress,
} from "./navigation";
import { MinimizedWizard, useWizards } from "./WizardProvider";
import { WizardResetConfirmation } from "./WizardResetConfirmation";

/** More minimized wizards than this collapse into a menu. */
export const MAX_STACKED_WIZARDS = 2;

/** How many cards the dock shows, so the page can leave room for them. */
export function getWizardDockCardCount(wizardCount: number): number {
  return wizardCount > MAX_STACKED_WIZARDS ? 1 : wizardCount;
}

export type WizardDockFocusRequest = { sessionId: string };

const cardClassName =
  "bg-surface-default border-borderSubtle shadow-elevation-200 w-80 rounded-lg border p-2";

type WizardDockProps = {
  wizards: MinimizedWizard[];
  /**
   * Moves focus to the session's row, or to the menu trigger when the dock
   * is collapsed. Pass a new object to focus again.
   */
  focusRequest?: WizardDockFocusRequest | null;
};

export function WizardDock({ wizards, focusRequest }: WizardDockProps) {
  const { discardWizard } = useWizards();
  const containerRef = useRef<HTMLDivElement>(null);
  const [pendingDiscard, setPendingDiscard] = useState<string | null>(null);

  useEffect(() => {
    if (!focusRequest) {
      return;
    }
    // The closing drawer restores focus on its own timer. Wait a tick so this
    // runs after it; otherwise the drawer moves focus to the page body.
    const timer = window.setTimeout(() => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const row = [
        ...container.querySelectorAll<HTMLElement>("[data-wizard-session-id]"),
      ].find(
        (element) => element.dataset.wizardSessionId === focusRequest.sessionId,
      );
      (row ?? container.querySelector<HTMLElement>("button"))?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [focusRequest]);

  const requestDiscard = ({ definition, session }: MinimizedWizard) => {
    if (
      session.completedAt !== null ||
      !sessionHasProgress(definition, session)
    ) {
      discardWizard(session.id);
    } else {
      setPendingDiscard(session.id);
    }
  };

  return (
    <>
      {wizards.length > 0 && (
        <div
          ref={containerRef}
          aria-label="Minimized wizards"
          role="region"
          className="z-nav fixed right-6 bottom-6 flex flex-col items-end gap-2"
        >
          {wizards.length > MAX_STACKED_WIZARDS ? (
            <WizardMenu wizards={wizards} onDiscard={requestDiscard} />
          ) : (
            wizards.map((wizard) => (
              <div key={wizard.session.id} className={cardClassName}>
                <WizardDockRow wizard={wizard} onDiscard={requestDiscard} />
              </div>
            ))
          )}
        </div>
      )}
      <WizardResetConfirmation
        pending={pendingDiscard ? "discard" : null}
        onCancel={() => setPendingDiscard(null)}
        onConfirm={() => {
          if (pendingDiscard) {
            discardWizard(pendingDiscard);
          }
          setPendingDiscard(null);
        }}
      />
    </>
  );
}

function WizardMenu({
  wizards,
  onDiscard,
}: {
  wizards: MinimizedWizard[];
  onDiscard: (wizard: MinimizedWizard) => void;
}) {
  const [open, setOpen] = useState(false);
  const inProgressCount = wizards.filter(
    ({ session }) => session.completedAt === null,
  ).length;
  const icons = wizards.slice(0, 3);

  return (
    <Popover
      rootProps={{ open, onOpenChange: setOpen }}
      triggerProps={{
        "aria-label": `${wizards.length} wizards. Select one to resume.`,
        className: `${cardClassName} hover:bg-surface-hover gap-3 p-4 text-left`,
      }}
      contentProps={{
        side: "top",
        align: "end",
        className: "z-popover w-80 max-w-none p-2",
      }}
      trigger={
        <>
          <span className="flex shrink-0 gap-1">
            {icons.map(({ definition, session }) =>
              definition.mascot ? (
                <definition.mascot.Art
                  key={session.id}
                  className="ring-borderSubtle size-8 ring-1"
                />
              ) : (
                <span
                  key={session.id}
                  className="bg-surface-default border-borderSubtle flex size-8 items-center justify-center rounded-full border"
                >
                  <definition.Icon
                    className="text-iconDefault size-icon-sm"
                    aria-hidden
                  />
                </span>
              ),
            )}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-label-sm text-textPrimary">
              {wizards.length} wizards
            </span>
            <span className="text-body-xs text-textTertiary">
              {inProgressCount === wizards.length
                ? "In progress"
                : `${inProgressCount} in progress`}
            </span>
          </span>
          <ChevronUp
            className={`text-iconDefault size-icon-md shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          />
        </>
      }
    >
      <ul
        aria-label="Wizards"
        className="flex max-h-96 flex-col overflow-y-auto"
      >
        {wizards.map((wizard) => (
          <li key={wizard.session.id}>
            <WizardDockRow
              wizard={wizard}
              onExpand={() => setOpen(false)}
              onDiscard={onDiscard}
            />
          </li>
        ))}
      </ul>
    </Popover>
  );
}

function WizardDockRow({
  wizard,
  onExpand,
  onDiscard,
}: {
  wizard: MinimizedWizard;
  onExpand?: () => void;
  onDiscard: (wizard: MinimizedWizard) => void;
}) {
  const { definition, session } = wizard;
  const { expandWizard } = useWizards();
  const { Icon } = definition;
  const subtitle = definition.getSubtitle?.(session.data);
  const isCompleted = session.completedAt !== null;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-wizard-session-id={session.id}
        onClick={() => {
          onExpand?.();
          expandWizard(session.id);
        }}
        className="hover:bg-surface-hover flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg p-2 text-left"
      >
        {definition.mascot ? (
          <definition.mascot.Art className="size-8" />
        ) : (
          <Icon
            className="text-iconDefault size-icon-lg shrink-0"
            aria-hidden
          />
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-label-sm text-textPrimary truncate">
            {definition.title}
          </span>
          <span className="text-body-xs text-textTertiary truncate">
            {isCompleted ? "Complete" : (subtitle ?? "In progress")}
          </span>
          <WizardDockRowProgress wizard={wizard} />
        </span>
      </button>
      <IconButton
        aria-label={`${isCompleted ? "Dismiss" : "Discard"} ${definition.title}`}
        size="sm"
        onClick={() => onDiscard(wizard)}
      >
        <X className="size-icon-sm" strokeWidth={2} aria-hidden />
      </IconButton>
    </div>
  );
}

function WizardDockRowProgress({
  wizard: { definition, session },
}: {
  wizard: MinimizedWizard;
}) {
  const { done, total } = getStepProgress(
    getRecordedApplicableSteps(definition.steps, session),
    session,
  );
  return (
    <span aria-hidden className="block">
      <Progress
        value={done}
        max={Math.max(total, 1)}
        className="bg-surface-muted h-1.5"
        indicatorClassName="bg-action-primary duration-500"
      />
    </span>
  );
}
